<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <Teleport to="body" :disabled="!isAppendBody">
    <!-- 使用单个 transition 包裹整个抽屉 -->
    <transition
      :name="`drawer-slide-fade-${placement}`"
      @after-enter="handleAfterEnter"
      @after-leave="handleAfterLeave"
    >
      <div
        v-show="modelValue"
        :class="['drawer-wrapper', !isAppendBody ? 'drawer-wrapper-no-body' : '']"
        @keydown.esc="handleEsc"
        tabindex="-1"
      >
        <!-- 遮罩层 -->
        <div
          v-if="isShowMask"
          v-show="modelValue"
          class="drawer-mask"
          @click.self="handleMaskClick"
        ></div>
        
        <!-- 抽屉主体 -->
        <div
          class="drawer-container"
          :class="`drawer-${placement}`"
          :style="{ 
            width: isUseParentWidth ? '' : (typeof width === 'number' ? `${width}px` : width),
            boxShadow: isShadow ? '' : 'none'
          }"
          role="dialog"
          aria-modal="true"
          :aria-label="title"
        >
          <!-- 头部 -->
          <div class="drawer-header" v-if="isTitle">
            <slot name="title">
              <span class="drawer-title">{{ title }}</span>
            </slot>
            <button
              v-if="showClose"
              class="drawer-close-btn"
              @click="handleClose"
              aria-label="关闭抽屉"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 4L4 12M4 4L12 12"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
          
          <!-- 内容区域 -->
          <div class="drawer-body">
            <slot></slot>
          </div>
          
          <!-- 底部（可选） -->
          <div v-if="$slots.footer" class="drawer-footer">
            <slot name="footer"></slot>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { defineProps, defineEmits, onMounted, onUnmounted, nextTick, ref, watch } from 'vue'

const props = defineProps({
  // 控制抽屉显示/隐藏
  modelValue: {
    type: Boolean,
    default: false
  },
  // 是否添加到body
  isAppendBody: {
    type: Boolean,
    default: true
  },
  // 是否显示遮罩层
  isShowMask: {
    type: Boolean,
    default: true
  },
  // 是否显示标题
  isTitle: {
    type: Boolean,
    default: true
  },
  // 抽屉标题
  title: {
    type: String,
    default: '抽屉标题'
  },
  // 抽屉宽度
  width: {
    type: [String, Number],
    default: '400px'
  },
  // 是否显示关闭按钮
  showClose: {
    type: Boolean,
    default: true
  },
  // 点击遮罩层是否可以关闭
  maskClosable: {
    type: Boolean,
    default: true
  },
  // 是否允许按ESC键关闭
  closeOnPressEscape: {
    type: Boolean,
    default: true
  },
  // 是否禁止滚动穿透
  lockScroll: {
    type: Boolean,
    default: true
  },
  // 🔥 新增：抽屉滑入方向，可选 'left' 或 'right'
  placement: {
    type: String,
    default: 'right',
    validator: (value) => ['left', 'right'].includes(value)
  },
  // 是否使用父元素的宽度，为true的话宽度设置无效
  isUseParentWidth: {
    type: Boolean,
    default: false
  },
  // 是否显示阴影
  isShadow: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['update:modelValue', 'close', 'open', 'opened', 'closed'])

// 控制滚动穿透
let scrollTop = 0

const unlockBodyScroll = () => {
  if (props.lockScroll) {
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.width = ''
    document.body.style.overflow = ''
    document.documentElement.scrollTop = scrollTop
    document.body.scrollTop = scrollTop
  }
}

// 关闭抽屉
const handleClose = () => {
  emit('update:modelValue', false)
  emit('close')
}

// 点击遮罩层
const handleMaskClick = () => {
  if (props.maskClosable) {
    handleClose()
  }
}

// ESC键处理
const handleEsc = (e) => {
  if (props.closeOnPressEscape && e.key === 'Escape' && props.modelValue) {
    handleClose()
  }
}

// 动画结束后触发的事件
const handleAfterEnter = () => {
  emit('opened')
}

const handleAfterLeave = () => {
  emit('closed')
  unlockBodyScroll()
}

// 监听键盘事件
onMounted(() => {
  window.addEventListener('keydown', handleEsc)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleEsc)
})

// 监听 modelValue 变化
const prevBodyOverflow = ref('')
const prevBodyPosition = ref('')

watch(() => props.modelValue, (newVal) => {
  if (newVal) {
    emit('open')
    nextTick(() => {
      // 抽屉打开后，将焦点设置到抽屉容器
      const drawerEl = document.querySelector('.drawer-wrapper')
      if (drawerEl) {
        drawerEl.focus()
      }
    })
    
    // 锁定滚动
    if (props.lockScroll) {
      prevBodyOverflow.value = document.body.style.overflow
      prevBodyPosition.value = document.body.style.position
      document.body.style.overflow = 'hidden'
    }
  } else {
    // 关闭时恢复滚动
    if (props.lockScroll) {
      document.body.style.overflow = prevBodyOverflow.value
      document.body.style.position = prevBodyPosition.value
    }
  }
})
</script>

<style scoped>
.drawer-wrapper {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2000;
  outline: none;
}

.drawer-wrapper-no-body, .drawer-wrapper-no-body .drawer-container {
  position: relative;
}
.drawer-wrapper-no-body, .drawer-wrapper-no-body .drawer-container, .drawer-wrapper-no-body .drawer-body {
  height: 100%;
}
.drawer-mask {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.5);
  transition: opacity 0.3s ease;
}

.drawer-container {
  position: absolute;
  top: 0;
  bottom: 0;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: transform 0.3s cubic-bezier(0.7, 0.3, 0.1, 1);
}

/* 🔥 新增：根据 placement 设置位置和阴影 */
.drawer-right {
  right: 0;
  box-shadow: -2px 0 12px rgba(0, 0, 0, 0.15);
}

.drawer-left {
  left: 0;
  box-shadow: 2px 0 12px rgba(0, 0, 0, 0.15);
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e8e8e8;
  flex-shrink: 0;
}

.drawer-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  line-height: 1.5;
}

.drawer-close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  color: #999;
  transition: all 0.3s;
  padding: 0;
}

.drawer-close-btn:hover {
  color: #666;
  background-color: #f5f5f5;
}

.drawer-body {
  flex: 1;
  padding: 10px 5px;
  overflow: auto;
  font-size: 14px;
  color: #333;
  line-height: 1.5;
  background: #f9f9f9;
}

.drawer-footer {
  padding: 16px 24px;
  border-top: 1px solid #e8e8e8;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

/* 🔥 修改：动态动画名称，支持左右方向 */
.drawer-slide-fade-right-enter-active,
.drawer-slide-fade-right-leave-active,
.drawer-slide-fade-left-enter-active,
.drawer-slide-fade-left-leave-active {
  transition: all 0.3s ease;
}

.drawer-slide-fade-right-enter-active .drawer-mask,
.drawer-slide-fade-right-leave-active .drawer-mask,
.drawer-slide-fade-left-enter-active .drawer-mask,
.drawer-slide-fade-left-leave-active .drawer-mask {
  transition: opacity 0.3s ease;
}

.drawer-slide-fade-right-enter-active .drawer-container,
.drawer-slide-fade-right-leave-active .drawer-container,
.drawer-slide-fade-left-enter-active .drawer-container,
.drawer-slide-fade-left-leave-active .drawer-container {
  transition: transform 0.3s cubic-bezier(0.7, 0.3, 0.1, 1);
}

/* 🔥 右边滑入动画 */
.drawer-slide-fade-right-enter-from {
  opacity: 0;
}

.drawer-slide-fade-right-enter-from .drawer-mask {
  opacity: 0;
}

.drawer-slide-fade-right-enter-from .drawer-container {
  transform: translateX(100%);
}

.drawer-slide-fade-right-enter-to {
  opacity: 1;
}

.drawer-slide-fade-right-enter-to .drawer-mask {
  opacity: 1;
}

.drawer-slide-fade-right-enter-to .drawer-container {
  transform: translateX(0);
}

.drawer-slide-fade-right-leave-from {
  opacity: 1;
}

.drawer-slide-fade-right-leave-from .drawer-mask {
  opacity: 1;
}

.drawer-slide-fade-right-leave-from .drawer-container {
  transform: translateX(0);
}

.drawer-slide-fade-right-leave-to {
  opacity: 0;
}

.drawer-slide-fade-right-leave-to .drawer-mask {
  opacity: 0;
}

.drawer-slide-fade-right-leave-to .drawer-container {
  transform: translateX(100%);
}

/* 🔥 左边滑入动画 */
.drawer-slide-fade-left-enter-from {
  opacity: 0;
}

.drawer-slide-fade-left-enter-from .drawer-mask {
  opacity: 0;
}

.drawer-slide-fade-left-enter-from .drawer-container {
  transform: translateX(-100%);
}

.drawer-slide-fade-left-enter-to {
  opacity: 1;
}

.drawer-slide-fade-left-enter-to .drawer-mask {
  opacity: 1;
}

.drawer-slide-fade-left-enter-to .drawer-container {
  transform: translateX(0);
}

.drawer-slide-fade-left-leave-from {
  opacity: 1;
}

.drawer-slide-fade-left-leave-from .drawer-mask {
  opacity: 1;
}

.drawer-slide-fade-left-leave-from .drawer-container {
  transform: translateX(0);
}

.drawer-slide-fade-left-leave-to {
  opacity: 0;
}

.drawer-slide-fade-left-leave-to .drawer-mask {
  opacity: 0;
}

.drawer-slide-fade-left-leave-to .drawer-container {
  transform: translateX(-100%);
}
</style>